// no template — prototype components (s6-kicker, z-button, ui-pill); the guide has no screen in approved-workspace.html
import { useEffect, useRef, type KeyboardEvent } from 'react'
import { ProtoButton } from '@/features/d0/states'
import Summary from './Summary'
import { useTutorialUi } from './open'
import type { TutorialState } from './machine'

export interface ChapterDrawerProps {
  state: TutorialState
  hasProgress: boolean
  onClose: () => void
  onRestart: (chapter: string) => void
  onResume: () => void
  onStartOver: () => void
}

// The guide drawer: summary, replay any chapter, resume, or start from the beginning.
export default function ChapterDrawer({ state, hasProgress, onClose, onRestart, onResume, onStartOver }: ChapterDrawerProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true })
    return () => {
      const invoker = useTutorialUi.getState().invoker
      if (invoker?.isConnected) invoker.focus({ preventScroll: true })
    }
  }, [])

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    e.preventDefault()
    onClose()
  }

  const running = ['waiting', 'showing', 'paused', 'chapterDone'].includes(state.mode)

  return (
    <div className="t0-drawer" role="dialog" aria-modal="false" aria-label="Workspace guide" onKeyDown={onKeyDown}>
      <div className="t0-drawer__head">
        <h2 className="t0-drawer__title" ref={headingRef} tabIndex={-1}>Workspace guide</h2>
        <ProtoButton className="z-quiet" onClick={onClose}>Close</ProtoButton>
      </div>
      <p className="t0-drawer__text">
        Eighteen short chapters walk through ZUPA on your own data. The guide never saves, generates or sends anything by itself.
      </p>
      <div className="t0-drawer__resume" data-tutorial-anchor="tutorial-resume">
        <ProtoButton className="z-primary" onClick={onResume}>{running ? 'Resume' : hasProgress ? 'Resume the guide' : 'Start the guide'}</ProtoButton>
        <ProtoButton onClick={onStartOver}>Start from beginning</ProtoButton>
      </div>
      <Summary state={state} onRestart={onRestart} />
    </div>
  )
}
